// src/pages/finance-portal/compliance-filings/ComplianceFilings.js

import React, { useState } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card, Grid,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Tabs, Tab, Alert, CircularProgress, MenuItem, Chip,
} from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import DescriptionIcon from '@mui/icons-material/Description';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const headCellSx = {
    fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b',
    textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0',
};

const bodyCellSx = {
    fontFamily, fontSize: '0.85rem', color: '#1e293b', borderBottom: '1px solid #f1f5f9',
};

const inputSx = {
    '& .MuiOutlinedInput-root': { fontFamily, borderRadius: '10px' },
    '& .MuiInputLabel-root': { fontFamily },
};

function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = [];
for (let y = currentYear; y >= currentYear - 5; y--) YEAR_OPTIONS.push(y);

const QUARTER_OPTIONS = [
    { value: 1, label: 'Q1 (Jan-Mar)' },
    { value: 2, label: 'Q2 (Apr-Jun)' },
    { value: 3, label: 'Q3 (Jul-Sep)' },
    { value: 4, label: 'Q4 (Oct-Dec)' },
];

const US_STATES = [
    '', 'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC',
];

// ─── Form 941 Tab ────────────────────────────────────────────
function Form941Tab() {
    const { enqueueSnackbar } = useSnackbar();
    const [taxYear, setTaxYear] = useState(currentYear);
    const [quarter, setQuarter] = useState(Math.ceil((new Date().getMonth() + 1) / 3));
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);

    const handleGenerate = async () => {
        setLoading(true);
        setError(null);
        setData(null);
        try {
            const res = await axiosInstance.post('/api/finance/payroll/compliance/form-941', {
                tax_year: taxYear,
                quarter,
            });
            if (res.data.success) {
                setData(res.data.data || {});
                enqueueSnackbar('Form 941 generated', { variant: 'success' });
            }
        } catch (err) {
            const msg = err.response?.data?.message || 'Failed to generate Form 941';
            setError(msg);
            enqueueSnackbar(msg, { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const lineItems = data?.form_941_data || data?.line_items || [];

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3, flexWrap: 'wrap' }}>
                <TextField
                    select label="Tax Year" value={taxYear}
                    onChange={(e) => setTaxYear(parseInt(e.target.value, 10))}
                    size="small" sx={{ ...inputSx, minWidth: 130 }}
                >
                    {YEAR_OPTIONS.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                </TextField>
                <TextField
                    select label="Quarter" value={quarter}
                    onChange={(e) => setQuarter(parseInt(e.target.value, 10))}
                    size="small" sx={{ ...inputSx, minWidth: 180 }}
                >
                    {QUARTER_OPTIONS.map((q) => <MenuItem key={q.value} value={q.value}>{q.label}</MenuItem>)}
                </TextField>
                <Button
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <DescriptionIcon />}
                    onClick={handleGenerate}
                    disabled={loading}
                    sx={{
                        fontFamily, fontWeight: 700, borderRadius: '10px',
                        bgcolor: ACCENT, textTransform: 'none', px: 3,
                        '&:hover': { bgcolor: '#16a34a' },
                    }}
                >
                    {loading ? 'Generating...' : 'Generate'}
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2, fontFamily }}>{error}</Alert>}

            {data && (
                <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <Box sx={{ p: 2.5, borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>
                            Form 941 - Employer's Quarterly Federal Tax Return
                        </Typography>
                        <Chip label={`Q${quarter} ${taxYear}`} size="small"
                            sx={{ fontFamily, fontWeight: 600, bgcolor: '#dbeafe', color: '#1e40af' }} />
                    </Box>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={headCellSx}>Line</TableCell>
                                    <TableCell sx={headCellSx}>Description</TableCell>
                                    <TableCell sx={headCellSx} align="right">Amount</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Array.isArray(lineItems) && lineItems.length > 0 ? (
                                    lineItems.map((item, idx) => (
                                        <TableRow key={idx} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                            <TableCell sx={bodyCellSx}>{item.line || item.line_number || idx + 1}</TableCell>
                                            <TableCell sx={bodyCellSx}>{item.description || item.label || '—'}</TableCell>
                                            <TableCell sx={bodyCellSx} align="right">
                                                {typeof item.amount === 'number' ? formatCurrency(item.amount) : (item.amount || '—')}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    /* Show summary fields if line_items not provided */
                                    Object.entries(data.form_941_data || data).filter(([k]) => k !== 'line_items').map(([key, val], idx) => (
                                        <TableRow key={idx} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                            <TableCell sx={bodyCellSx}>{idx + 1}</TableCell>
                                            <TableCell sx={bodyCellSx}>{key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</TableCell>
                                            <TableCell sx={bodyCellSx} align="right">
                                                {typeof val === 'number' ? formatCurrency(val) : String(val)}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}
        </Box>
    );
}

// ─── Form 940 Tab ────────────────────────────────────────────
function Form940Tab() {
    const { enqueueSnackbar } = useSnackbar();
    const [taxYear, setTaxYear] = useState(currentYear);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);

    const handleGenerate = async () => {
        setLoading(true);
        setError(null);
        setData(null);
        try {
            const res = await axiosInstance.post('/api/finance/payroll/compliance/form-940', {
                tax_year: taxYear,
            });
            if (res.data.success) {
                setData(res.data.data || {});
                enqueueSnackbar('Form 940 generated', { variant: 'success' });
            }
        } catch (err) {
            const msg = err.response?.data?.message || 'Failed to generate Form 940';
            setError(msg);
            enqueueSnackbar(msg, { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const lineItems = data?.form_940_data || data?.line_items || [];

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3, flexWrap: 'wrap' }}>
                <TextField
                    select label="Tax Year" value={taxYear}
                    onChange={(e) => setTaxYear(parseInt(e.target.value, 10))}
                    size="small" sx={{ ...inputSx, minWidth: 130 }}
                >
                    {YEAR_OPTIONS.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                </TextField>
                <Button
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <DescriptionIcon />}
                    onClick={handleGenerate}
                    disabled={loading}
                    sx={{
                        fontFamily, fontWeight: 700, borderRadius: '10px',
                        bgcolor: ACCENT, textTransform: 'none', px: 3,
                        '&:hover': { bgcolor: '#16a34a' },
                    }}
                >
                    {loading ? 'Generating...' : 'Generate'}
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2, fontFamily }}>{error}</Alert>}

            {data && (
                <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <Box sx={{ p: 2.5, borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>
                            Form 940 - Employer's Annual Federal Unemployment (FUTA) Tax Return
                        </Typography>
                        <Chip label={`${taxYear}`} size="small"
                            sx={{ fontFamily, fontWeight: 600, bgcolor: '#dbeafe', color: '#1e40af' }} />
                    </Box>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={headCellSx}>Line</TableCell>
                                    <TableCell sx={headCellSx}>Description</TableCell>
                                    <TableCell sx={headCellSx} align="right">Amount</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Array.isArray(lineItems) && lineItems.length > 0 ? (
                                    lineItems.map((item, idx) => (
                                        <TableRow key={idx} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                            <TableCell sx={bodyCellSx}>{item.line || item.line_number || idx + 1}</TableCell>
                                            <TableCell sx={bodyCellSx}>{item.description || item.label || '—'}</TableCell>
                                            <TableCell sx={bodyCellSx} align="right">
                                                {typeof item.amount === 'number' ? formatCurrency(item.amount) : (item.amount || '—')}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    Object.entries(data.form_940_data || data).filter(([k]) => k !== 'line_items').map(([key, val], idx) => (
                                        <TableRow key={idx} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                            <TableCell sx={bodyCellSx}>{idx + 1}</TableCell>
                                            <TableCell sx={bodyCellSx}>{key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</TableCell>
                                            <TableCell sx={bodyCellSx} align="right">
                                                {typeof val === 'number' ? formatCurrency(val) : String(val)}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}
        </Box>
    );
}

// ─── State Withholding Tab ───────────────────────────────────
function StateWithholdingTab() {
    const { enqueueSnackbar } = useSnackbar();
    const [taxYear, setTaxYear] = useState(currentYear);
    const [quarter, setQuarter] = useState(Math.ceil((new Date().getMonth() + 1) / 3));
    const [state, setState] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);

    const handleGenerate = async () => {
        setLoading(true);
        setError(null);
        setData(null);
        try {
            const payload = { tax_year: taxYear, quarter };
            if (state) payload.state = state;

            const res = await axiosInstance.post('/api/finance/payroll/compliance/state-withholding', payload);
            if (res.data.success) {
                setData(res.data.data || {});
                enqueueSnackbar('State withholding data generated', { variant: 'success' });
            }
        } catch (err) {
            const msg = err.response?.data?.message || 'Failed to generate state withholding';
            setError(msg);
            enqueueSnackbar(msg, { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const stateBreakdown = data?.states || data?.state_breakdown || [];

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3, flexWrap: 'wrap' }}>
                <TextField
                    select label="Tax Year" value={taxYear}
                    onChange={(e) => setTaxYear(parseInt(e.target.value, 10))}
                    size="small" sx={{ ...inputSx, minWidth: 130 }}
                >
                    {YEAR_OPTIONS.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                </TextField>
                <TextField
                    select label="Quarter" value={quarter}
                    onChange={(e) => setQuarter(parseInt(e.target.value, 10))}
                    size="small" sx={{ ...inputSx, minWidth: 180 }}
                >
                    {QUARTER_OPTIONS.map((q) => <MenuItem key={q.value} value={q.value}>{q.label}</MenuItem>)}
                </TextField>
                <TextField
                    select label="State (optional)" value={state}
                    onChange={(e) => setState(e.target.value)}
                    size="small" sx={{ ...inputSx, minWidth: 160 }}
                >
                    <MenuItem value="">All States</MenuItem>
                    {US_STATES.filter(Boolean).map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </TextField>
                <Button
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <DescriptionIcon />}
                    onClick={handleGenerate}
                    disabled={loading}
                    sx={{
                        fontFamily, fontWeight: 700, borderRadius: '10px',
                        bgcolor: ACCENT, textTransform: 'none', px: 3,
                        '&:hover': { bgcolor: '#16a34a' },
                    }}
                >
                    {loading ? 'Generating...' : 'Generate'}
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2, fontFamily }}>{error}</Alert>}

            {data && (
                <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <Box sx={{ p: 2.5, borderBottom: '1px solid #e2e8f0' }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>
                            State Withholding Breakdown - Q{quarter} {taxYear}
                        </Typography>
                    </Box>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={headCellSx}>State</TableCell>
                                    <TableCell sx={headCellSx} align="right">Employees</TableCell>
                                    <TableCell sx={headCellSx} align="right">Taxable Wages</TableCell>
                                    <TableCell sx={headCellSx} align="right">Tax Withheld</TableCell>
                                    <TableCell sx={headCellSx}>Filing Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Array.isArray(stateBreakdown) && stateBreakdown.length > 0 ? (
                                    stateBreakdown.map((row, idx) => (
                                        <TableRow key={idx} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                            <TableCell sx={{ ...bodyCellSx, fontWeight: 700 }}>{row.state || '—'}</TableCell>
                                            <TableCell sx={bodyCellSx} align="right">{row.employee_count ?? row.employees ?? '—'}</TableCell>
                                            <TableCell sx={bodyCellSx} align="right">{formatCurrency(row.taxable_wages)}</TableCell>
                                            <TableCell sx={bodyCellSx} align="right">{formatCurrency(row.tax_withheld || row.withholding)}</TableCell>
                                            <TableCell sx={bodyCellSx}>
                                                <Chip
                                                    label={row.filing_status || row.status || 'Pending'}
                                                    size="small"
                                                    sx={{
                                                        fontFamily, fontWeight: 700, fontSize: '0.7rem',
                                                        bgcolor: (row.filing_status || row.status) === 'FILED' ? '#dcfce7' : '#fef3c7',
                                                        color: (row.filing_status || row.status) === 'FILED' ? '#166534' : '#92400e',
                                                    }}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={5} sx={{ ...bodyCellSx, textAlign: 'center', py: 4 }}>
                                            No state withholding data available
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}
        </Box>
    );
}

// ─── State Unemployment Tab ──────────────────────────────────
function StateUnemploymentTab() {
    const { enqueueSnackbar } = useSnackbar();
    const [taxYear, setTaxYear] = useState(currentYear);
    const [quarter, setQuarter] = useState(Math.ceil((new Date().getMonth() + 1) / 3));
    const [state, setState] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);

    const handleGenerate = async () => {
        setLoading(true);
        setError(null);
        setData(null);
        try {
            const payload = { tax_year: taxYear, quarter };
            if (state) payload.state = state;

            const res = await axiosInstance.post('/api/finance/payroll/compliance/state-unemployment', payload);
            if (res.data.success) {
                setData(res.data.data || {});
                enqueueSnackbar('State unemployment data generated', { variant: 'success' });
            }
        } catch (err) {
            const msg = err.response?.data?.message || 'Failed to generate state unemployment';
            setError(msg);
            enqueueSnackbar(msg, { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const stateBreakdown = data?.states || data?.state_breakdown || [];

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3, flexWrap: 'wrap' }}>
                <TextField
                    select label="Tax Year" value={taxYear}
                    onChange={(e) => setTaxYear(parseInt(e.target.value, 10))}
                    size="small" sx={{ ...inputSx, minWidth: 130 }}
                >
                    {YEAR_OPTIONS.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                </TextField>
                <TextField
                    select label="Quarter" value={quarter}
                    onChange={(e) => setQuarter(parseInt(e.target.value, 10))}
                    size="small" sx={{ ...inputSx, minWidth: 180 }}
                >
                    {QUARTER_OPTIONS.map((q) => <MenuItem key={q.value} value={q.value}>{q.label}</MenuItem>)}
                </TextField>
                <TextField
                    select label="State (optional)" value={state}
                    onChange={(e) => setState(e.target.value)}
                    size="small" sx={{ ...inputSx, minWidth: 160 }}
                >
                    <MenuItem value="">All States</MenuItem>
                    {US_STATES.filter(Boolean).map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </TextField>
                <Button
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <DescriptionIcon />}
                    onClick={handleGenerate}
                    disabled={loading}
                    sx={{
                        fontFamily, fontWeight: 700, borderRadius: '10px',
                        bgcolor: ACCENT, textTransform: 'none', px: 3,
                        '&:hover': { bgcolor: '#16a34a' },
                    }}
                >
                    {loading ? 'Generating...' : 'Generate'}
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2, fontFamily }}>{error}</Alert>}

            {data && (
                <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <Box sx={{ p: 2.5, borderBottom: '1px solid #e2e8f0' }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>
                            State Unemployment Breakdown - Q{quarter} {taxYear}
                        </Typography>
                    </Box>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={headCellSx}>State</TableCell>
                                    <TableCell sx={headCellSx} align="right">Employees</TableCell>
                                    <TableCell sx={headCellSx} align="right">Taxable Wages</TableCell>
                                    <TableCell sx={headCellSx} align="right">SUI Rate</TableCell>
                                    <TableCell sx={headCellSx} align="right">Tax Due</TableCell>
                                    <TableCell sx={headCellSx}>Filing Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Array.isArray(stateBreakdown) && stateBreakdown.length > 0 ? (
                                    stateBreakdown.map((row, idx) => (
                                        <TableRow key={idx} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                            <TableCell sx={{ ...bodyCellSx, fontWeight: 700 }}>{row.state || '—'}</TableCell>
                                            <TableCell sx={bodyCellSx} align="right">{row.employee_count ?? row.employees ?? '—'}</TableCell>
                                            <TableCell sx={bodyCellSx} align="right">{formatCurrency(row.taxable_wages)}</TableCell>
                                            <TableCell sx={bodyCellSx} align="right">
                                                {row.sui_rate != null ? `${(row.sui_rate * 100).toFixed(2)}%` : (row.rate || '—')}
                                            </TableCell>
                                            <TableCell sx={bodyCellSx} align="right">{formatCurrency(row.tax_due || row.tax_amount)}</TableCell>
                                            <TableCell sx={bodyCellSx}>
                                                <Chip
                                                    label={row.filing_status || row.status || 'Pending'}
                                                    size="small"
                                                    sx={{
                                                        fontFamily, fontWeight: 700, fontSize: '0.7rem',
                                                        bgcolor: (row.filing_status || row.status) === 'FILED' ? '#dcfce7' : '#fef3c7',
                                                        color: (row.filing_status || row.status) === 'FILED' ? '#166534' : '#92400e',
                                                    }}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={6} sx={{ ...bodyCellSx, textAlign: 'center', py: 4 }}>
                                            No state unemployment data available
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}
        </Box>
    );
}

// ─── Main Component ──────────────────────────────────────────
export default function ComplianceFilings() {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(0);

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                        <GavelIcon sx={{ color: 'rgba(255,255,255,0.9)', fontSize: 32 }} />
                        <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.75rem', color: '#fff' }}>
                            Compliance Filings
                        </Typography>
                    </Box>
                    <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.95rem' }}>
                        Generate Form 941, Form 940, state withholding, and state unemployment filings
                    </Typography>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, maxWidth: 1400, mx: 'auto' }}>
                {/* Tabs */}
                <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', mb: 3, overflow: 'hidden' }}>
                    <Tabs
                        value={activeTab}
                        onChange={(_, v) => setActiveTab(v)}
                        sx={{
                            borderBottom: '1px solid #e2e8f0',
                            '& .MuiTab-root': {
                                fontFamily, fontWeight: 600, textTransform: 'none',
                                fontSize: '0.9rem', color: '#64748b',
                            },
                            '& .Mui-selected': { color: '#16a34a' },
                            '& .MuiTabs-indicator': { bgcolor: '#16a34a' },
                        }}
                    >
                        <Tab label="Form 941" />
                        <Tab label="Form 940" />
                        <Tab label="State Withholding" />
                        <Tab label="State Unemployment" />
                    </Tabs>

                    <Box sx={{ p: 3 }}>
                        {activeTab === 0 && <Form941Tab />}
                        {activeTab === 1 && <Form940Tab />}
                        {activeTab === 2 && <StateWithholdingTab />}
                        {activeTab === 3 && <StateUnemploymentTab />}
                    </Box>
                </Paper>
            </Box>
        </Box>
    );
}
